import Fuse, { type IFuseOptions } from 'fuse.js';

/**
 * Item unificado que puede ir al SearchPalette. `kind` decide el icono, la
 * ruta destino y el sección del grupo de resultados.
 */
export interface SearchItem {
  kind: 'group' | 'expense' | 'member' | 'tag';
  id: string;
  title: string;
  subtitle?: string | undefined;
  url: string;
  // Campo secundario para boost de matches: p.ej. tags de un expense, email de
  // un miembro. Fuse busca en `title` (peso alto) + `keywords` (peso bajo).
  keywords?: string[] | undefined;
}

const FUSE_OPTIONS: IFuseOptions<SearchItem> = {
  keys: [
    { name: 'title', weight: 0.7 },
    { name: 'subtitle', weight: 0.2 },
    { name: 'keywords', weight: 0.1 },
  ],
  threshold: 0.4, // 0 = match exacto; 1 = todo pasa. 0.4 tolera typos leves.
  ignoreLocation: true,
  minMatchCharLength: 2,
};

/**
 * Construye un Fuse index sobre una lista de items. Memoiza fuera del hook —
 * el índice es costoso de crear si hay miles de expenses.
 */
export function buildIndex(items: SearchItem[]): Fuse<SearchItem> {
  return new Fuse(items, FUSE_OPTIONS);
}

/**
 * Wrapper de Fuse.search que devuelve items limitados por sección para no
 * saturar la palette (max 5 por kind).
 */
export function search(fuse: Fuse<SearchItem>, query: string, maxPerKind = 5): SearchItem[] {
  if (!query.trim()) return [];
  const results = fuse.search(query, { limit: 50 });
  const buckets: Record<SearchItem['kind'], SearchItem[]> = {
    group: [],
    expense: [],
    member: [],
    tag: [],
  };
  for (const r of results) {
    const kind = r.item.kind;
    if (buckets[kind].length < maxPerKind) buckets[kind].push(r.item);
  }
  return [...buckets.group, ...buckets.expense, ...buckets.member, ...buckets.tag];
}

// ==================== Recent items (localStorage) ====================

const RECENT_KEY = '__search_recent';
const RECENT_MAX = 8;

interface RecentEntry {
  kind: SearchItem['kind'];
  id: string;
  title: string;
  subtitle?: string | undefined;
  url: string;
  at: number;
}

export function getRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentEntry[];
    return Array.isArray(parsed) ? parsed.slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

export function addRecent(item: SearchItem): void {
  try {
    const now = Date.now();
    const current = getRecent().filter((r) => !(r.kind === item.kind && r.id === item.id));
    const next: RecentEntry[] = [
      { kind: item.kind, id: item.id, title: item.title, subtitle: item.subtitle, url: item.url, at: now },
      ...current,
    ].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // localStorage bloqueado / lleno → no-op, funciona sin persistencia
  }
}

export function clearRecent(): void {
  try {
    localStorage.removeItem(RECENT_KEY);
  } catch {
    // no-op
  }
}
